import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Star, Smiley, SmileySad, TrendUp } from "@phosphor-icons/react";

const Stars = ({ n }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} weight={i <= n ? "fill" : "regular"} className={i <= n ? "text-amber-500" : "text-muted-foreground/40"} />
    ))}
  </span>
);

const npsColor = (v) => (v >= 50 ? "text-green-600" : v >= 0 ? "text-amber-600" : "text-rose-600");

const SummaryCard = ({ icon: Icon, label, value, accent, sub }) => (
  <Card className="rounded-sm p-6 border-border" data-testid={`fstat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="label-caps">{label}</p>
        <p className="font-heading text-3xl font-bold mt-2">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${accent}`}><Icon size={20} weight="bold" /></div>
    </div>
  </Card>
);

export default function Feedback() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/feedback/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/feedback").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <PageHeader title="Feedback & NPS" subtitle="Customer ratings and Net Promoter Score" />
      <div className="p-8 space-y-8">
        {summary && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <SummaryCard icon={TrendUp} label="NPS Score" value={summary.nps} accent="bg-blue-100 text-blue-700"
                sub={<span className={npsColor(summary.nps)}>{summary.nps >= 50 ? "Excellent" : summary.nps >= 0 ? "Good" : "Needs attention"}</span>} />
              <SummaryCard icon={Star} label="Avg Service Rating" value={`${summary.avg_service_rating} / 5`} accent="bg-amber-100 text-amber-700" />
              <SummaryCard icon={Smiley} label="Total Responses" value={summary.total_responses} accent="bg-green-100 text-green-700" />
              <SummaryCard icon={SmileySad} label="Promoters / Detractors" value={`${summary.promoters} / ${summary.detractors}`} accent="bg-violet-100 text-violet-700"
                sub={`${summary.passives} passive`} />
            </div>

            <Card className="rounded-sm border-border">
              <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold">Partner-wise Ratings</h3></div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead>Partner</TableHead><TableHead className="text-center">Responses</TableHead>
                    <TableHead className="text-center">NPS</TableHead><TableHead className="text-center">Avg Rating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="partner-ratings-table">
                  {summary.by_partner.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No ratings yet</TableCell></TableRow>}
                  {summary.by_partner.map((p) => (
                    <TableRow key={p.partner_id} className="hover:bg-secondary/30">
                      <TableCell className="font-semibold">{p.partner_name}</TableCell>
                      <TableCell className="text-center">{p.count}</TableCell>
                      <TableCell className={`text-center font-mono font-semibold ${npsColor(p.nps)}`}>{p.nps}</TableCell>
                      <TableCell className="text-center"><span className="inline-flex items-center gap-1.5"><Stars n={Math.round(p.avg_partner_rating)} /> <span className="text-xs text-muted-foreground">{p.avg_partner_rating}</span></span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        <Card className="rounded-sm border-border">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold">All Feedback</h3></div>
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Customer</TableHead><TableHead>Service</TableHead><TableHead>Partner</TableHead>
                <TableHead className="text-center">NPS</TableHead><TableHead>Service</TableHead>
                <TableHead>Partner ★</TableHead><TableHead>Comment</TableHead><TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="feedback-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No feedback yet</TableCell></TableRow>}
              {rows.map((f) => (
                <TableRow key={f.id} className="hover:bg-secondary/30">
                  <TableCell className="font-semibold">{f.customer_name}</TableCell>
                  <TableCell className="text-sm">{f.service_type}<div className="text-xs text-muted-foreground">{f.product_category}</div></TableCell>
                  <TableCell className="text-sm">{f.partner_name || "—"}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className={`rounded-sm font-mono ${f.nps_score >= 9 ? "border-green-300 text-green-700" : f.nps_score >= 7 ? "border-amber-300 text-amber-700" : "border-rose-300 text-rose-700"}`}>{f.nps_score}</Badge>
                  </TableCell>
                  <TableCell><Stars n={f.service_rating} /></TableCell>
                  <TableCell>{f.partner_rating ? <Stars n={f.partner_rating} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{f.comment || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(f.created_at || "").slice(0, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </>
  );
}
